import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { finalize, firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { ContadorOperarService } from '../../../../../services/contador/contador-operar.service';
import { TimbradoService } from '../../../../../services/configuracoes/timbrado.service';
import { CotacaoGuardService } from '../../../../../services/cotacao/cotacao-guard.service';
import { NotaService } from '../../../../../services/financeiro/notas.service';
import { NfeService } from '../../../../../services/financeiro/nfe.service';
import { PessoaDTO } from '../../../../../models/clientes/pessoa.model';
import { EntitySelectorConfig } from '../../../../../shared/components/entity-selector/entity-selector.interfaces';
import {
  ItemVendaSelecionado,
  OperarVendaItemSelectorComponent,
} from './operar-venda-item-selector/operar-venda-item-selector.component';
import {
  OperarVendaLinhaComponent,
  OrigemEdicao,
} from './operar-venda-linha/operar-venda-linha.component';
import { MOEDA_CONFIG, Moeda, MoedaConfig } from '../../../../../enums/moeda.enum';
import { MoedaDTO } from '../../../../../models/configuracoes/empresa.model';
import { toLocalISOString } from '../../../../../shared/utils/date-utils';
import { PessoaService } from '../../../../../services/pessoas/pessoas.service';
import { LanguageService } from '../../../../../shared/language.service';

/** Item adicionado ao carrinho da venda (linha da tabela, somente leitura). */
interface ItemCarrinho {
  itemId: number;
  nome: string;
  codigoItem?: string | null;
  tipoItem: string;
  tipoDeImposto: string | null;
  quantidade: number;
  valorUnitario: number;
  custoUnitario: number;
  precoDeVendaOriginal: number;
  saldo?: number | null;

  /**
   * O objeto CRU da API que originou esta linha, guardado para o botão EDITAR
   * poder devolvê-la à linha de lançamento.
   *
   * ⚠️ Sem ele, editar remontaria um produto sintético e perderia o
   * `precoDeVendaOriginal`: um item cujo preço veio do modal voltaria com
   * `precoDeVenda = valorUnitario`, e `onItemDoModal()` gravaria isso como
   * "preço antigo" — o que VAZA para a nota (`precoDeVendaAntigo` em
   * `salvar()`).
   *
   * Opcional porque linhas montadas em teste não o trazem; `editarLinha()` tem
   * fallback.
   */
  produtoCru?: any;
}

/** Opção do dropdown de timbrado (label formatada + entidade completa). */
interface TimbradoOption {
  label: string;
  value: any;
  id: number;
}

/**
 * O comprador chega SEMPRE como `PessoaConsultaDTO` — tanto de `/pessoas/buscar`
 * (usado pelo entity-selector) quanto de `/pessoas/{id}` —, cujo campo de cidade
 * é um OBJETO `cidade`. Mas o model do frontend (`PessoaDTO`) só declara
 * `cidade_id`, e `PessoaService.getById()` está tipado `Observable<PessoaDTO>`.
 * Este alias local reconcilia os dois: evita `as any` e documenta a divergência
 * no ponto exato onde ela importa, sem alterar o model global.
 */
type CompradorSelecionado = PessoaDTO & { cidade?: unknown };

/**
 * Aba "Nova venda" da tela Operar Cliente (Área do Contador).
 *
 * Cria uma NOTA COM FATURA LEGAL DE VENDA para o cliente selecionado, sob o
 * contexto dele (`X-Empresa-Contexto` injetado pelo interceptor) — a nota nasce
 * na empresa do cliente, não na do contador.
 *
 * Escopo deliberadamente enxuto (não reusa a tela de Vendas): só fatura legal,
 * à vista ou a prazo. Timbrado eletrônico transmite a NFe automaticamente.
 *
 * Recarrega moeda, timbrados, vendedores e cotação ao abrir a aba e ao trocar
 * de cliente.
 */
@Component({
  selector: 'app-operar-venda',
  templateUrl: './operar-venda.component.html',
  styleUrls: ['./operar-venda.component.scss'],
})
export class OperarVendaComponent implements OnChanges {
  /** Id da empresa do cliente — muda quando o contador troca de cliente. */
  @Input() clienteEmpresaId?: number | null;

  /** Indica se a aba está ativa; carrega os dados quando a aba abre. */
  @Input() ativo = false;

  /**
   * Emitido quando a venda grava a nota com sucesso. A LISTAGEM de notas vive no
   * componente pai (operar-cliente), que a recarrega ao receber este aviso —
   * sem ele a nota recém-criada só aparecia depois de um F5.
   */
  @Output() vendaConcluida = new EventEmitter<void>();

  /**
   * Emitido quando a transmissão da NFe se resolve — autorizada, rejeitada ou
   * com falha de comunicação. Só existe no caminho de timbrado ELETRÔNICO, onde
   * a situação da nota muda no servidor DEPOIS de ela já ter entrado na lista.
   *
   * É um Output separado de `vendaConcluida` de propósito: são dois fatos
   * distintos (a nota nasceu × a situação dela mudou), e o pai reage a cada um
   * de um jeito — a venda volta para a primeira página, a transmissão recarrega
   * a página em que o usuário está.
   */
  @Output() notaTransmitida = new EventEmitter<void>();

  // --- Estado de carregamento ---
  carregando = false;
  salvando = false;
  erro = false;

  // --- Moeda da empresa do cliente ---
  private moedaDto: MoedaDTO | null = null;
  moedaEnum: string = 'GUARANI';
  cfgMoeda: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];

  // --- Comprador (cliente da venda) ---
  // Usa o mesmo seletor rico da tela de Vendas (app-entity-selector).
  comprador: CompradorSelecionado | undefined;
  clienteSelectorConfig!: EntitySelectorConfig;

  /**
   * Indica se os dados do comprador foram REALMENTE carregados do servidor.
   *
   * O cliente padrão é pré-selecionado como um objeto SINTÉTICO com apenas `id`
   * e `nome` (ver `carregarConfig()`), então checar `ruc`/`ci`/`telefone`/
   * `cidade` nele acusaria "faltam os três" mesmo com o cadastro completo no
   * banco. Enquanto esta flag for `false`, a validação do frontend NÃO existe e
   * quem decide é o backend.
   */
  private compradorDetalhado = false;

  /** Chaves i18n dos campos que faltam ao comprador. Vazio = nada a avisar. */
  camposFaltantesComprador: string[] = [];

  /** Os campos faltantes já traduzidos e unidos ("RUC ou CI, telefone e cidade"). */
  textoCamposFaltantes = '';

  // --- Vendedor: apenas os usuários da empresa do cliente + o contador. ---
  vendedores: { label: string; value: number }[] = [];
  vendedorSelecionadoId: number | null = null;

  // --- Timbrado (obrigatório: define a fatura legal) ---
  timbrados: TimbradoOption[] = [];
  timbradoSelecionadoId: number | null = null;
  /** Id do timbrado padrão de venda da empresa do cliente (pré-seleção). */
  private timbradoPadraoVendaId: number | null = null;

  // --- Itens ---
  // A seleção usa o MESMO modal "Seleccionar Artículo" da tela de compra
  // (app-produto-selecionar), sob o contexto do cliente. Aqui só guardamos o
  // carrinho; qtd/preço ficam editáveis na tabela abaixo.
  mostrarSelecionarItem = false;
  carrinho: ItemCarrinho[] = [];

  /** Diálogo de cadastro rápido de cliente (o MESMO de `/vendas`). */
  mostrarDialogNovoCliente = false;

  /**
   * A LINHA DE LANÇAMENTO (Item | Qtd. | Preço unit. | Subtotal | +) é um
   * componente próprio: o estado da COMPOSIÇÃO — item escolhido, quantidade,
   * preço efetivo e pendência de edição — vive lá dentro.
   *
   * Este componente conserva o que é do NEGÓCIO: o carrinho, a soma de item
   * repetido, o modal de preço e a gravação da nota. A conversa é por eventos
   * (`lancar`, `precoAusente`, `devolverAoCarrinho`); os três métodos abaixo
   * são o que precisamos PEDIR a ele.
   */
  @ViewChild(OperarVendaLinhaComponent)
  private linhaLancamento?: OperarVendaLinhaComponent;

  /**
   * Diálogo "Criar novo Item" (`app-item-criar-rapido`), aberto pelo atalho no
   * rodapé do dropdown da busca. É o MESMO componente de `/adicionar-compra`,
   * aqui SEM `fornecedorId` — a venda não tem fornecedor.
   */
  mostrarCriarItem = false;

  /**
   * Referência ao modal de seleção, para PILOTÁ-LO quando o item escolhido na
   * busca inline não tem preço cadastrado (ver `abrirModalComItem`).
   *
   * Por TIPO e não por `#ref`: o componente já está declarado no template, então
   * a query resolve sem tocar no HTML. Só existe depois que o `*ngIf` do modal
   * materializa — daí os `?.` em todos os usos.
   */
  @ViewChild(OperarVendaItemSelectorComponent)
  private seletorModal?: OperarVendaItemSelectorComponent;

  // Diálogo para cadastrar a cotação do cliente quando ele ainda não tem nenhuma.

  // --- Completar cadastro do cliente (RUC/CI, telefone e cidade) ---

  /** Controla a exibição do diálogo de completar cadastro (banana-box com o filho). */
  mostrarCompletarCliente = false;

  /**
   * De onde o diálogo foi aberto. Só 'FINALIZACAO' retenta a venda — abrir na
   * SELEÇÃO e sair salvando registraria uma venda que o usuário nem montou.
   */
  private origemCompletarCliente: 'SELECAO' | 'FINALIZACAO' | null = null;

  /**
   * Ids de clientes a quem o diálogo JÁ foi oferecido na SELEÇÃO.
   *
   * Gravado na ABERTURA, não no cancelamento: abriu uma vez, não abre de novo,
   * independentemente de como o usuário fechou (Cancelar, ESC, clique fora).
   * NÃO é limpo por `resetarFormulario()` — sobrevive a vendas sucessivas na
   * mesma aba. Zera só quando o componente é recriado.
   */
  private clientesJaOferecidos = new Set<number>();

  /**
   * CIRCUIT BREAKER. Sem ele: o PUT responde sem os campos →
   * `atualizarValidacaoComprador()` recomputa "falta" → `validar()` reabre o
   * diálogo → laço infinito. UMA tentativa por venda; depois, toast e desiste.
   */
  private retryAposCompletarUsado = false;

  // --- Pagamento ---
  condicaoPagamento: 'CONTADO' | 'CREDITO' = 'CONTADO';
  numeroParcelas = 1;

  observacao = '';

  constructor(
    private operarService: ContadorOperarService,
    private timbradoService: TimbradoService,
    private cotacaoGuard: CotacaoGuardService,
    private notaService: NotaService,
    private nfeService: NfeService,
    private messageService: MessageService,
    private translate: TranslateService,
    private pessoaService: PessoaService,
    private languageService: LanguageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const abriuAgora =
      !!changes['ativo'] && this.ativo && changes['ativo'].previousValue !== true;
    const trocouCliente =
      !!changes['clienteEmpresaId'] && !changes['clienteEmpresaId'].firstChange;

    if (changes['ativo'] && !this.ativo) return;

    if (abriuAgora || (trocouCliente && this.ativo)) {
      this.resetarFormulario();
      this.carregarDados();
    }
  }

  // ===========================================================================
  // Carga de dados (sob o contexto do cliente)
  // ===========================================================================

  private carregarDados(): void {
    this.carregando = true;
    this.erro = false;
    this.construirConfigsSeletores();

    // A config vem primeiro: define a moeda, o comprador padrão e o timbrado
    // padrão. Só então carregamos timbrados (para pré-selecionar o padrão),
    // vendedores e o detalhe do comprador padrão em paralelo.
    //
    // `detalharComprador()` entra AQUI, junto dos demais, e não em sequência:
    // o custo passa a ser o `max()` das três chamadas, não a soma. Enquanto
    // elas estão em voo o formulário inteiro está fora do DOM
    // (`*ngIf="!carregando ..."` no template), então não há janela para o
    // usuário submeter a venda com o comprador ainda não detalhado.
    this.carregarConfig()
      .then(() =>
        Promise.all([
          this.carregarTimbrados(),
          this.carregarVendedores(),
          this.detalharComprador(),
        ]),
      )
      .then(() => {
        this.carregando = false;
      })
      .catch(() => {
        this.carregando = false;
        this.erro = true;
      });
  }

  recarregar(): void {
    this.carregarDados();
  }

  private async carregarConfig(): Promise<void> {
    const config = await firstValueFrom(this.operarService.buscarConfiguracao());

    this.moedaDto = config?.moeda ?? null;
    this.moedaEnum = this.moedaDto?.enum ?? 'GUARANI';
    this.cfgMoeda = MOEDA_CONFIG[this.moedaEnum as Moeda] ?? MOEDA_CONFIG[Moeda.GUARANI];

    // Pré-seleciona o comprador com o cliente padrão da empresa.
    if (config?.clientePadraoId) {
      this.comprador = {
        id: config.clientePadraoId,
        nome: config.clientePadraoNome ?? '',
      } as CompradorSelecionado;
      // Objeto SINTÉTICO (só id e nome): ainda não sabemos nada sobre o
      // cadastro dele. `detalharComprador()` completa isso logo em seguida.
      // O recomputo aqui é obrigatório (não redundante): `recarregar()` chama
      // `carregarDados()` SEM passar por `resetarFormulario()`, então sem esta
      // linha o estado derivado ANTIGO sobreviveria sobre um comprador novo.
      this.compradorDetalhado = false;
      this.atualizarValidacaoComprador();
    }

    // Guarda o timbrado padrão para pré-selecionar após carregar a lista.
    this.timbradoPadraoVendaId = config?.timbradoPadraoVendaId ?? null;
  }

  private async carregarTimbrados(): Promise<void> {
    const res: any = await firstValueFrom(this.timbradoService.get('/timbrado'));
    const lista = (res?.content ?? []).filter((t: any) => {
      const tipo = t?.tipoDeTransacao;
      const tipoEnum = tipo && typeof tipo === 'object' && 'enum' in tipo ? tipo.enum : tipo;
      return tipoEnum === 'VENDA';
    });

    this.timbrados = lista.map((t: any) => ({
      label: this.montarLabelTimbrado(t),
      value: t,
      id: t?.id ?? null,
    }));

    // Pré-seleção: 1º o timbrado padrão de venda da empresa; senão, o único disponível.
    const padrao = this.timbrados.find((t) => t.id === this.timbradoPadraoVendaId);
    if (padrao) {
      this.timbradoSelecionadoId = padrao.id;
    } else if (this.timbrados.length === 1) {
      this.timbradoSelecionadoId = this.timbrados[0].id;
    }
  }

  private async carregarVendedores(): Promise<void> {
    // Falha aqui (ex.: endpoint indisponível) não deve derrubar a aba inteira.
    const lista = await firstValueFrom(this.operarService.listarVendedoresParaVenda()).catch(
      () => [] as any[],
    );
    this.vendedores = (lista ?? []).map((v) => ({
      label: v.contador
        ? `${v.nome} ${this.translate.instant('contador_operar.venda.sufixo_contador')}`
        : v.nome,
      value: v.id,
    }));
    // Default: primeiro vendedor (usuário da empresa do cliente).
    if (this.vendedores.length && !this.vendedorSelecionadoId) {
      this.vendedorSelecionadoId = this.vendedores[0].value;
    }
  }

  private montarLabelTimbrado(t: any): string {
    const tipoNotaEnum =
      t?.tipoNota && typeof t.tipoNota === 'object' ? t.tipoNota.enum : (t?.tipoNota ?? '');
    const raw = this.translate.instant(`enums.tipo_padrao_nota.${tipoNotaEnum}`);
    const nomeTipoNota = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    const filial = String(t?.numeroFilial ?? '').padStart(3, '0');
    const expedicao = String(t?.pontoDeExpedicao ?? '').padStart(3, '0');
    const numero = String(t?.proximoNumero ?? '').padStart(7, '0');
    return `${nomeTipoNota} - ${filial}-${expedicao}-${numero}`;
  }

  // ===========================================================================
  // Comprador e vendedor (app-entity-selector, igual à tela de Vendas)
  // ===========================================================================

  /**
   * Config do seletor de comprador — a mesma EXPERIÊNCIA de `/vendas`, não só a
   * mesma classe.
   *
   * `showAddInDropdown` liga o rodapé "⊕ Adicionar Pessoa". `showAddButton` fica
   * `false`: ele renderiza um "+" avulso ao lado do campo, que `/vendas` também
   * não usa.
   *
   * ⚠️ O cadastro esteve desligado aqui sob a justificativa de que
   * `POST /pessoas` estaria fora da allowlist. Não se sustenta — a rota está nas
   * duas pontas (`contador-contexto.interceptor.ts:64`, que não filtra por
   * método, e `AuthorizationFilter.kt:126`), e o cliente nasce na empresa do
   * CLIENTE OPERADO.
   *
   * Sem checagem de permissão, deliberadamente: `temPermissao('cliente')` leria
   * a permissão do CONTADOR sobre a empresa DELE, que nada diz sobre a empresa
   * operada. Quem autoriza é o backend; esconder o atalho não protegeria nada.
   */
  private construirConfigsSeletores(): void {
    this.clienteSelectorConfig = {
      categoryFilter: 'categoriaCliente=true',
      placeholder: this.translate.instant('contador_operar.venda.comprador_placeholder'),
      showAddButton: false,
      showAddInDropdown: true,
      dialogTitle: this.translate.instant('contador_operar.venda.comprador'),
      dialogIcon: 'pi pi-users',
      emptyMessage: this.translate.instant('contador_operar.venda.nenhum_encontrado'),
    };
  }

  /** Abre o diálogo de cadastro rápido de cliente (link do rótulo e rodapé do dropdown). */
  abrirDialogNovoCliente(): void {
    this.mostrarDialogNovoCliente = true;
  }

  /**
   * Cliente recém-criado (ou vinculado por RUC) no diálogo de cadastro rápido.
   *
   * Reusa `onCompradorSelecionado()` LITERALMENTE, e é isso que garante a rede
   * de proteção: um cliente criado sem cidade/RUC/telefone cai na mesma
   * validação de sempre — hint âmbar e diálogo de "completar cadastro" —, sem
   * precisar tornar campos obrigatórios no diálogo compartilhado (que tem outros
   * consumidores). Duplicar a seleção aqui faria as duas portas divergirem.
   */
  onClienteCriado(pessoa: PessoaDTO): void {
    if (!pessoa?.id) return;
    this.onCompradorSelecionado(pessoa as CompradorSelecionado);
  }

  /**
   * Carrega o cadastro completo do comprador pré-selecionado (cliente padrão),
   * que `carregarConfig()` deixou como objeto sintético.
   *
   * O caminho do entity-selector NÃO precisa disto: `/pessoas/buscar` já devolve
   * o `PessoaConsultaDTO` completo, com ruc, ci, telefone e cidade.
   */
  private async detalharComprador(): Promise<void> {
    const id = this.comprador?.id;
    if (!id) return;

    // Falha aqui NUNCA bloqueia a venda nem derruba a aba: sem o detalhe, a
    // flag fica `false`, a validação do frontend é pulada e quem decide é o
    // backend. Mesmo tratamento defensivo já usado em `carregarVendedores()`.
    // ATENÇÃO: o endpoint devolve PessoaConsultaDTO (cidade OBJETO), apesar de
    // `getById()` estar tipado `Observable<PessoaDTO>`.
    const pessoa = await firstValueFrom(this.pessoaService.getById('/pessoas', String(id))).catch(
      () => null,
    );

    // Guarda de corrida: o usuário pode ter trocado (ou limpado) o comprador
    // enquanto a resposta estava em voo — nesse caso ela está obsoleta e
    // sobrescreveria a escolha dele.
    if (!pessoa || this.comprador?.id !== id) return;

    this.comprador = pessoa;
    this.compradorDetalhado = true;
    this.atualizarValidacaoComprador();
  }

  /**
   * Handler da seleção (e da limpeza) do comprador no entity-selector.
   *
   * `clear()` do seletor emite `undefined`, então este único caminho cobre
   * seleção e limpeza.
   */
  onCompradorSelecionado(pessoa: PessoaDTO | undefined): void {
    this.comprador = pessoa;
    // O `/pessoas/buscar` devolve PessoaConsultaDTO, que JÁ traz ruc, ci,
    // telefone e cidade — o objeto do seletor é validável sem chamada extra.
    this.compradorDetalhado = !!pessoa;
    // Comprador novo, tentativa nova: o breaker é POR VENDA, não por sessão.
    this.retryAposCompletarUsado = false;
    this.atualizarValidacaoComprador();

    // MOMENTO 1: só a escolha DELIBERADA de um cliente abre o diálogo. Pendurar
    // isto em `atualizarValidacaoComprador()` o abriria também na carga da aba
    // (pré-seleção do "Consumidor") e depois de CADA venda
    // (`salvar()` → `resetarFormulario()` + `carregarDados()`).
    const id = pessoa?.id;
    if (!id || this.camposFaltantesComprador.length === 0) return;
    if (this.clientesJaOferecidos.has(id)) return; // uma vez por cliente
    this.clientesJaOferecidos.add(id); // marca na ABERTURA
    this.abrirCompletarCliente('SELECAO');
  }

  /**
   * Abre o diálogo de completar cadastro. É o ÚNICO ponto que liga o circuit
   * breaker ao fallback em texto: fora daqui, ninguém precisa saber que ele existe.
   */
  private abrirCompletarCliente(origem: 'SELECAO' | 'FINALIZACAO'): void {
    // REGRA DE OURO: sem alvo, não bloqueia e não abre modal nenhum.
    if (!this.comprador?.id) return;

    // Segunda passagem pela finalização: o PUT já foi feito e os campos continuam
    // faltando. Reabrir o diálogo aqui seria um laço — avisa e desiste, deixando o
    // backend decidir.
    if (origem === 'FINALIZACAO' && this.retryAposCompletarUsado) {
      this.avisar('contador_operar.venda.cliente_incompleto_apos_tentativa', {
        campos: this.textoCamposFaltantes,
      });
      return;
    }

    this.origemCompletarCliente = origem;
    this.mostrarCompletarCliente = true;
  }

  /**
   * Chamado quando o diálogo grava o cadastro do cliente.
   *
   * 5º (e último) escritor de `comprador` — ver a invariante em
   * `atualizarValidacaoComprador()`. Recebe o cadastro DEVOLVIDO PELO SERVIDOR,
   * nunca o que foi digitado.
   */
  onClienteAtualizado(pessoa: CompradorSelecionado): void {
    const origem = this.origemCompletarCliente;
    this.origemCompletarCliente = null;
    this.mostrarCompletarCliente = false;

    // Guarda de corrida, mesmo critério de `detalharComprador()`: se o comprador
    // mudou (ou sumiu) enquanto o diálogo estava aberto, a resposta está obsoleta
    // e sobrescreveria a escolha atual.
    if (!pessoa?.id || this.comprador?.id !== pessoa.id) return;

    this.comprador = pessoa;
    this.compradorDetalhado = true;
    this.atualizarValidacaoComprador(); // INVARIANTE — mesmo bloco

    if (origem !== 'FINALIZACAO') return; // o momento 1 apenas fecha

    // Liga o breaker ANTES de retentar. Se o PUT tiver respondido sem os campos,
    // o `validar()` da retentativa cai no toast, não em outro diálogo.
    this.retryAposCompletarUsado = true;
    this.salvar(); // mesmo desenho de `onCotacaoSalva()`
  }

  /**
   * ÚNICO escritor de `camposFaltantesComprador` e `textoCamposFaltantes`.
   *
   * INVARIANTE: estes dois campos e a flag `compradorDetalhado` são um único
   * estado. Nenhum ponto do componente pode escrever `this.comprador` ou a flag
   * sem chamar este método no mesmo bloco. São 5 os escritores de `comprador`:
   * `carregarConfig()`, `detalharComprador()`, `onCompradorSelecionado()`,
   * `resetarFormulario()` e `onClienteAtualizado()`. Manter assim torna a
   * invariante estrutural, e não apenas disciplinar.
   *
   * O diálogo de completar cadastro NÃO fura a invariante: ele recebe `pessoaId`
   * (escalar) e devolve um objeto novo pelo @Output — nunca escreve `comprador`.
   */
  private atualizarValidacaoComprador(): void {
    this.camposFaltantesComprador = this.calcularCamposFaltantes();
    this.textoCamposFaltantes = this.formatarLista(
      this.camposFaltantesComprador.map((chave) => this.translate.instant(chave)),
    );
  }

  /**
   * Chaves i18n dos campos que faltam ao comprador para a fatura legal.
   *
   * Espelha, linha a linha, `PessoaService.verificarDadosDaPessoaFaturaLegal`
   * do backend. A tela envia `ehFaturaLegal: true` e `tipoDeTransacao: 'VENDA'`
   * fixos, então lá a checagem é INCONDICIONAL — não existe caso em que esta
   * validação seja mais restritiva que a do servidor.
   */
  private calcularCamposFaltantes(): string[] {
    // REGRA DE OURO: na dúvida, NÃO bloquear. Sem comprador, ou sem os dados
    // dele carregados, devolve vazio e deixa o backend decidir.
    if (!this.comprador?.id || !this.compradorDetalhado) return [];

    const vazio = (v: unknown) => v == null || String(v).trim() === '';
    const faltantes: string[] = [];

    // RUC e CI são ALTERNATIVOS no backend (`&&`, não `||`): falta só quando os
    // DOIS estão vazios. Copiar `vendas.component.ts:2065` aqui — que testa só
    // o `ruc` — barraria indevidamente quem tem apenas CI.
    if (vazio(this.comprador.ruc) && vazio(this.comprador.ci)) {
      faltantes.push('contador_operar.venda.campo_ruc_ci');
    }
    if (vazio(this.comprador.telefone)) {
      faltantes.push('contador_operar.venda.campo_telefone');
    }
    // Os dois caminhos (buscar e {id}) devolvem PessoaConsultaDTO, cujo campo é
    // `cidade` OBJETO; `cidade_id` é seguro-morto para uma futura troca de DTO.
    // Deliberadamente permissivo: QUALQUER valor não-nulo conta como presente,
    // sem inspecionar a forma. Falso negativo é aceitável (o backend recusa);
    // falso positivo, que bloquearia uma venda legítima, não é.
    if (this.comprador.cidade_id == null && this.comprador.cidade == null) {
      faltantes.push('contador_operar.venda.campo_cidade');
    }
    return faltantes;
  }

  /** Une os campos com vírgulas e o conectivo do idioma ("e" / "y"). */
  private formatarLista(itens: string[]): string {
    try {
      return new Intl.ListFormat(this.languageService.getCurrent(), {
        style: 'long',
        type: 'conjunction',
      }).format(itens);
    } catch {
      // NÃO REMOVER: o projeto não tem `.browserslistrc`, então vale o
      // browserslist padrão do Angular 17, que inclui op_mini, and_uc 15.5 e
      // kaios 2.5 — alvos SEM `Intl.ListFormat`. Sem este fallback o componente
      // lançaria justamente ao montar a mensagem de erro.
      return itens.join(', ');
    }
  }

  // ===========================================================================
  // Itens (modal "Seleccionar Artículo" + carrinho)
  // ===========================================================================

  /** Abre o popup de seleção de artigo da venda, sob contexto do cliente. */
  abrirSelecionarItem(): void {
    this.mostrarSelecionarItem = true;
  }

  /**
   * Atalho "Criar novo item" do rodapé do dropdown da busca.
   *
   * Fecha o modal de seleção antes de abrir o de criação: se o usuário chegou
   * aqui com o seletor aberto, os dois `p-dialog` empilhariam sem `baseZIndex`
   * definido e o de baixo continuaria capturando o foco.
   */
  abrirCriarItem(): void {
    this.mostrarSelecionarItem = false;
    this.mostrarCriarItem = true;
  }

  /**
   * `(criado)` do `app-item-criar-rapido`: o item nasce SEM preço de venda
   * (`precoDeVenda: 0`, o diálogo não tem esse campo) e, pela regra vigente do
   * carrinho, item a zero NÃO entra na venda (`precoInvalido` no seletor).
   *
   * Por isso o encadeamento reusa `abrirModalComItem()` — exatamente o mesmo
   * caminho já validado em runtime para o item SEM PREÇO escolhido na busca:
   * abre o seletor com o item pré-selecionado e o cursor no campo de preço.
   *
   * Nada de `@Input` novo no seletor: `abrirModalComItem()` injeta o item pelo
   * `ViewChild` (`selecionarLinha(produto)`), então NÃO depende de o item novo
   * aparecer na primeira página da lista lazy — ele é passado como objeto.
   */
  onItemCriado(itemCriado: any): void {
    if (!itemCriado?.id) return;
    this.abrirModalComItem(itemCriado);
  }

  /**
   * `(devolverAoCarrinho)` da linha de lançamento: recoloca no carrinho, na
   * POSIÇÃO ORIGINAL, a linha cuja edição foi abandonada (`×` no campo, texto
   * apagado, outro item escolhido ou outra linha posta em edição).
   *
   * É o que impede que "editar" vire "apagar" para quem desistiu no meio.
   * A linha chega opaca (`unknown`) porque a faixa de lançamento não conhece o
   * carrinho — ela só devolve o que recebeu.
   */
  restaurarLinhaNoCarrinho(origem: OrigemEdicao): void {
    const linha = origem?.linha as ItemCarrinho | undefined;
    if (!linha) return;

    const copia = [...this.carrinho];
    copia.splice(Math.min(origem.indice, copia.length), 0, linha);
    this.carrinho = copia;
  }

  /**
   * Botão EDITAR da linha do carrinho: devolve o item à composição.
   *
   * DESENHO — "mover com volta": editar RETIRA a linha do carrinho e a carrega
   * na faixa; o `+` devolve com os valores novos e o `×` restaura como estava.
   *
   * ⚠️ Deixar a linha no carrinho e fazer o `+` "atualizar" NÃO funciona:
   * `onItemDoModal()` SOMA quando o item já está lá, então confirmar 12 sobre
   * uma linha de 5 daria 17. Retirar a linha é o que mantém o `+` como caminho
   * único. (Mover sem volta foi descartado por virar um "apagar" silencioso.)
   *
   * Faixa já ocupada: a composição é descartada, mas se ERA uma edição a linha
   * volta ao carrinho antes. Nunca se perde algo já lançado.
   */
  editarLinha(index: number): void {
    const alvo = this.carrinho[index];
    if (!alvo) return;

    // Descarta a composição atual ANTES de mexer no array: se ela era uma
    // edição, a linha correspondente volta ao carrinho (via
    // `devolverAoCarrinho`) e os índices mudam — por isso o alvo é reencontrado
    // por REFERÊNCIA logo abaixo.
    this.linhaLancamento?.limparComposicao();

    const i = this.carrinho.indexOf(alvo);
    if (i < 0) return;

    this.carrinho = this.carrinho.filter((_, k) => k !== i);

    // `produtoCru` é o objeto da API; o fallback cobre linhas sem ele (ver a
    // interface). O preço vai à parte porque a linha pode ter um valor que não
    // está no cadastro do item (preço informado no modal).
    this.linhaLancamento?.editar({
      produto: alvo.produtoCru ?? this.produtoSinteticoDe(alvo),
      quantidade: alvo.quantidade,
      preco: alvo.valorUnitario,
      origem: { linha: alvo, indice: i },
    });
  }

  /**
   * Reconstrói um "produto" a partir da linha, para o caso de ela não ter o
   * `produtoCru` (linhas de teste). `tipoItem`/`tipoDeImposto` vão como string
   * porque o `onItemDoModal()` já aceita as duas formas (`?.enum ?? valor`).
   */
  private produtoSinteticoDe(linha: ItemCarrinho): any {
    return {
      id: linha.itemId,
      nome: linha.nome,
      codigoItem: linha.codigoItem,
      tipoItem: linha.tipoItem,
      tipoDeImposto: linha.tipoDeImposto,
      custo: linha.custoUnitario,
      precoDeVenda: linha.precoDeVendaOriginal,
      saldo: linha.saldo,
    };
  }

  /**
   * Abre o modal de seleção JÁ com o item escolhido na busca e o foco no campo
   * de preço. O item só entra no carrinho quando o usuário confirmar o modal —
   * este método não chama `onItemDoModal()`, e o `confirmar()` do modal recusa
   * preço zero.
   *
   * ⚠️ São TRÊS disputantes de foco, e a cadeia de setTimeout existe por causa
   * disso:
   *   1. `pAutoFocus` da busca do modal enfileira um foco em
   *      ngAfterContentChecked E em ngAfterViewChecked, e só se marca satisfeito
   *      dentro do callback — o nível interno do duplo setTimeout roda depois;
   *   2. o `p-dialog` (`focusOnShow`) tem mecanismo PRÓPRIO: captura o elemento
   *      com `[autofocus]` no início da animação (quando `itemSelecionado` ainda
   *      é `null`) e agenda o foco em `setTimeout(..., 150)` fora da NgZone
   *      (`primeng-dialog.mjs:441-448`). Nenhum `setTimeout(0)` vence isso — daí
   *      os 200ms do nível mais interno.
   *   3. `[autofocus]="!itemSelecionado"` sozinho não basta: a captura do
   *      p-dialog acontece antes de o binding mudar.
   *
   * Um flicker é esperado (a busca recebe foco por alguns ms antes do preço) —
   * é o custo de não editar o .ts do modal.
   */
  // Público desde a extração da faixa de lançamento: agora é o template que o
  // liga ao `(precoAusente)` do `app-operar-venda-linha` — antes era chamado só
  // daqui de dentro.
  abrirModalComItem(produto: any): void {
    this.mostrarSelecionarItem = true;

    setTimeout(() => {
      setTimeout(() => {
        const ref = this.seletorModal;
        // Degradação aceita: sem a referência o modal abre vazio e o usuário
        // escolhe o item na mão. Nada quebra.
        if (!ref) return;

        ref.busca = this.termoParaModal(produto);
        ref.onBuscaChange(); // traz o item para a lista visível
        ref.selecionarLinha(produto); // seleciona e agenda o foco no preço

        // Reafirmação do foco: precisa rodar DEPOIS dos 150ms do
        // `focusOnShow` do p-dialog (ver comentário acima) — por isso 200ms,
        // e não um setTimeout(0) como as demais reafirmações desta cadeia.
        setTimeout(() => ref.inputPreco?.input?.nativeElement?.focus(), 200);
      });
    });
  }

  /**
   * Termo que dirige a lista do modal.
   *
   * Usa o NOME (bem mais seletivo que o código num filtro OR com LIKE) e TRUNCA
   * no primeiro caractere que corromperia a query: o modal monta o `filter` sem
   * encodar e o ApiService repassa esse parâmetro cru, então um `&` no nome
   * viraria um parâmetro novo e um `%` pode virar 400.
   */
  private termoParaModal(produto: any): string {
    const nome = String(produto?.nome ?? '')
      .split(/[&#+%=?]/)[0]
      .trim();
    return nome || String(produto?.codigoItem ?? '');
  }

  /**
   * Handler do `(selecionado)` do popup de venda: adiciona a linha ao carrinho
   * com a quantidade e o preço de venda escolhidos no popup. Se o item já
   * estiver no carrinho, soma a quantidade.
   */
  onItemDoModal(sel: ItemVendaSelecionado): void {
    const produto = sel?.item;
    if (!produto?.id) return;

    const existente = this.carrinho.find((c) => c.itemId === produto.id);
    if (existente) {
      existente.quantidade += sel.quantidade;
      existente.valorUnitario = sel.valorUnitario;
    } else {
      this.carrinho = [
        ...this.carrinho,
        {
          itemId: produto.id,
          nome: produto.nome,
          codigoItem: produto.codigoItem ?? null,
          tipoItem: produto.tipoItem?.enum ?? produto.tipoItem ?? 'MERCADORIA',
          tipoDeImposto: produto.tipoDeImposto?.enum ?? produto.tipoDeImposto ?? null,
          quantidade: sel.quantidade,
          valorUnitario: sel.valorUnitario,
          custoUnitario: Number(produto.custo ?? 0),
          precoDeVendaOriginal: Number(produto.precoDeVenda ?? sel.valorUnitario),
          saldo: produto.saldo ?? null,
          // Guardado para o botão Editar poder devolver a linha à composição
          // sem remontar o item (ver `ItemCarrinho.produtoCru`).
          produtoCru: produto,
        },
      ];
    }
    this.mostrarSelecionarItem = false;
  }

  removerItem(index: number): void {
    this.carrinho = this.carrinho.filter((_, i) => i !== index);
  }

  subtotalItem(item: ItemCarrinho): number {
    return (item.quantidade || 0) * (item.valorUnitario || 0);
  }

  total(): number {
    return this.carrinho.reduce((soma, item) => soma + this.subtotalItem(item), 0);
  }

  private custoTotal(): number {
    return this.carrinho.reduce(
      (soma, item) => soma + (item.quantidade || 0) * (item.custoUnitario || 0),
      0,
    );
  }

  // ===========================================================================
  // Salvar (criar a nota / fatura legal de venda)
  // ===========================================================================

  private timbradoSelecionado(): any | null {
    return this.timbrados.find((t) => t.id === this.timbradoSelecionadoId)?.value ?? null;
  }

  private ehNotaEletronica(tipoNota: string): boolean {
    return tipoNota === 'ELETRONICA';
  }

  private casasDecimais(): number {
    return this.moedaDto?.casasDecimais ?? (this.moedaEnum === 'GUARANI' ? 0 : 2);
  }

  async salvar(): Promise<void> {
    if (!this.validar()) return;

    this.salvando = true;
    try {
      const cotacaoId = await this.buscarCotacaoId();
      if (!cotacaoId) {
        // Cliente sem cotação e o usuário cancelou o diálogo de cadastro (o guard
        // já avisou). A venda não é registrada.
        return;
      }

      const payload = this.montarPayload(cotacaoId);
      const resposta = await firstValueFrom(this.notaService.postParaVenda('/nota', payload));
      const notaId = resposta?.id || null;

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('common.sucesso'),
        detail: this.translate.instant('contador_operar.venda.sucesso'),
      });

      // Avisa o pai para recarregar a listagem de notas (ela vive lá).
      this.vendaConcluida.emit();

      // Timbrado eletrônico: transmite a NFe automaticamente (segue o padrão do timbrado).
      if (notaId && this.ehNotaEletronica(payload.tipoNota)) {
        this.transmitirNfe(notaId);
      }

      // `resetarFormulario()` é também onde o circuit breaker do diálogo de
      // completar cadastro é zerado — e esta é a ÚNICA porta por onde uma venda
      // concluída passa. Não zerar o breaker aqui também: estado zerado em dois
      // lugares diverge com o tempo.
      this.resetarFormulario();
      // Recarrega tudo: atualiza o próximo número da fatura e restaura os
      // padrões (comprador padrão, vendedor, timbrado padrão) para a próxima venda.
      this.carregarDados();
    } catch (e) {
      // Erros HTTP (4xx/5xx) são exibidos pelo HttpErrorInterceptor global.
    } finally {
      this.salvando = false;
    }
  }

  private validar(): boolean {
    if (!this.comprador?.id) {
      this.avisar('contador_operar.venda.selecione_comprador');
      return false;
    }
    // Rede de segurança do hint inline: impede o POST /nota que o backend
    // recusaria com 400 e, agora, OFERECE A CORREÇÃO em vez de só avisar.
    // Posição preservada de propósito: `salvar()` só faz `this.salvando = true`
    // DEPOIS de `validar()`, então abrir o diálogo daqui não trava o botão
    // "Registrar venda" em loading.
    if (this.camposFaltantesComprador.length > 0) {
      this.abrirCompletarCliente('FINALIZACAO');
      return false;
    }
    if (!this.vendedorSelecionadoId) {
      this.avisar('contador_operar.venda.selecione_vendedor');
      return false;
    }
    if (!this.timbradoSelecionadoId) {
      this.avisar('contador_operar.venda.selecione_timbrado');
      return false;
    }
    if (this.carrinho.length === 0) {
      this.avisar('contador_operar.venda.carrinho_vazio');
      return false;
    }
    if (this.carrinho.some((i) => (i.quantidade || 0) <= 0)) {
      this.avisar('contador_operar.venda.quantidade_invalida');
      return false;
    }
    // Rede de segurança: o popup já exige o preço quando o item não tem preço
    // cadastrado, mas uma nota fiscal com item a zero não pode ser gravada em
    // hipótese alguma (o total sairia zerado).
    if (this.carrinho.some((i) => (i.valorUnitario || 0) <= 0)) {
      this.avisar('contador_operar.venda.preco_invalido');
      return false;
    }
    if (this.condicaoPagamento === 'CREDITO' && (!this.numeroParcelas || this.numeroParcelas < 1)) {
      this.avisar('contador_operar.venda.parcelas_invalidas');
      return false;
    }
    return true;
  }

  private avisar(chave: string, params?: object): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('common.atencao'),
      detail: this.translate.instant(chave, params),
    });
  }

  /**
   * Garante a cotação do CLIENTE (solicita via diálogo global quando não há),
   * sob o contexto dele: `moedaEmpresa`/`empresaId` do cliente, pois a sessão
   * responde pela empresa do CONTADOR. Mantém o fallback GUARANI histórico.
   */
  private async buscarCotacaoId(): Promise<number | null> {
    const cotacao = await this.cotacaoGuard.garantirCotacao({
      moeda: this.moedaEnum,
      moedaEmpresa: this.moedaEnum,
      empresaId: this.clienteEmpresaId ?? null,
      fallbackGuarani: true,
    });
    return cotacao?.id ?? null;
  }

  private montarPayload(cotacaoId: number): any {
    const timbrado = this.timbradoSelecionado();
    const agora = new Date();
    const total = this.total();

    const itens = this.carrinho.map((item) => {
      const valorTotal = this.subtotalItem(item);
      return {
        item_id: item.itemId,
        tipoDeImposto: item.tipoDeImposto,
        tipoItem: item.tipoItem,
        quantidade: item.quantidade,
        valorUnitario: item.valorUnitario,
        custoUnitario: item.custoUnitario,
        custoTotal: item.custoUnitario * item.quantidade,
        valorTotal: valorTotal,
        desconto: 0,
        valorUnitarioComDesconto: item.valorUnitario,
        valorTotalComDesconto: valorTotal,
        precoDeVendaNovo: item.valorUnitario,
        precoDeVendaAntigo: item.precoDeVendaOriginal,
        observacao: 'Observação',
        modificadores: [],
        variantesDePreco: [],
        itemDePedido_id: null,
      };
    });

    const valorTotalEmServicos = itens
      .filter((i) => i.tipoItem === 'SERVICO')
      .reduce((s, i) => s + i.valorTotalComDesconto, 0);
    const valorTotalEmMercadorias = itens
      .filter((i) => i.tipoItem !== 'SERVICO')
      .reduce((s, i) => s + i.valorTotalComDesconto, 0);

    const payload: any = {
      emissao: toLocalISOString(agora),
      emissaoFatura: toLocalISOString(agora),
      tipoDeTransacao: 'VENDA',
      ehFaturaLegal: true,
      numeroFatura: timbrado?.proximoNumero || null,
      numeroPontoExpedicao: timbrado?.pontoDeExpedicao || null,
      numeroFilial: timbrado?.numeroFilial || null,
      tipoNota: timbrado?.tipoNota?.enum ?? timbrado?.tipoNota ?? 'VENDA_COMUM',
      condicaoDePagamento: this.condicaoPagamento,
      cotacao_id: cotacaoId,
      custoTotalDosItens: this.custoTotal(),
      valorTotal: total,
      valorDoDesconto: 0,
      tipoDeDesconto: 'SEM_DESCONTO',
      porcentagemDesconto: 0,
      valorTotalComDesconto: total,
      valorRecebidoNaConta: 0,
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmReal: 0,
      valorRecebidoEmPeso: 0,
      informacaoDaTransacao: '',
      moeda: this.moedaEnum,
      cancelado: false,
      pessoa_id: this.comprador?.id || null,
      vendedor_id: this.vendedorSelecionadoId,
      timbrado_id: timbrado?.id || null,
      numeroTimbrado: timbrado?.timbrado || null,
      observacao: this.observacao || '',
      pedidoId: null,
      itemDeNota: itens,
      valorTotalEmServicos,
      valorTotalEmMercadorias,
      chaveNotaEletronica: '',
      baixas: [],
      titulos: [],
      numeroParcelas: 0,
    };

    if (this.condicaoPagamento === 'CONTADO') {
      // Pagamento à vista, integral, na moeda da empresa do cliente.
      this.aplicarValorRecebidoNaMoeda(payload, total);
    } else {
      // Crédito: gera as parcelas (títulos a receber).
      payload.numeroParcelas = this.numeroParcelas;
      payload.titulos = this.gerarTitulos(total);
    }

    return payload;
  }

  /** Seta o valorRecebidoEm{Moeda} = total no campo correspondente à moeda da empresa. */
  private aplicarValorRecebidoNaMoeda(payload: any, total: number): void {
    switch (this.moedaEnum) {
      case 'REAL':
        payload.valorRecebidoEmReal = total;
        break;
      case 'DOLAR':
        payload.valorRecebidoEmDolar = total;
        break;
      case 'PESO':
        payload.valorRecebidoEmPeso = total;
        break;
      default:
        payload.valorRecebidoEmGuarani = total;
    }
  }

  /** Gera os títulos das parcelas (mesma distribuição da tela de Vendas). */
  private gerarTitulos(valorTotal: number): any[] {
    const dec = this.casasDecimais();
    const factor = Math.pow(10, dec);
    const totalInCents = Math.round(valorTotal * factor);

    const base = Math.floor(totalInCents / this.numeroParcelas);
    const resto = totalInCents - base * (this.numeroParcelas - 1);
    const valores = Array.from({ length: this.numeroParcelas }, (_, i) =>
      (i === this.numeroParcelas - 1 ? resto : base) / factor,
    );

    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);
    const dia = baseDate.getDate();

    const vencimentos = Array.from({ length: this.numeroParcelas }, (_, i) => {
      const d = new Date(baseDate);
      d.setMonth(d.getMonth() + (i + 1));
      if (d.getDate() !== dia) d.setDate(0);
      // ISO local (YYYY-MM-DDT00:00:00), NÃO dd/MM/yyyy: o backend desserializa
      // este campo como LocalDateTime e devolvia 400 em toda venda a prazo.
      // Mesmo formatador que `emissao` já usa logo abaixo.
      return toLocalISOString(d);
    });

    return valores.map((v, index) => ({
      emissao: toLocalISOString(),
      vencimento: vencimentos[index],
      saldo: valorTotal - valores.slice(0, index).reduce((s, x) => s + x, 0),
      valor: v,
      numeroParcela: index + 1,
      tipoReceita: 'VENDAS_A_PRAZO',
      tipoOperacaoFinanceira: 'ENTRADA',
      moeda: this.moedaEnum,
      pessoa_id: this.comprador?.id || null,
      situacaoDeCobranca: 'ABERTO',
    }));
  }

  private transmitirNfe(notaId: number): void {
    this.nfeService
      .post(`/nfe/transmitir/${notaId}`, undefined)
      // `finalize` e nao `next`: a transmissao muda a SITUACAO da nota no
      // servidor tanto quando autoriza quanto quando rejeita/falha, e a listagem
      // precisa mostrar o resultado real nos dois casos. Este e o SEGUNDO aviso
      // ao pai — o primeiro (vendaConcluida) ja fez a nota aparecer na lista
      // assim que o POST gravou. So o caminho eletronico chega aqui, entao a
      // venda nao eletronica continua com uma unica recarga.
      .pipe(finalize(() => this.notaTransmitida.emit()))
      .subscribe({
        next: () =>
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('contador_operar.venda.nfe_transmitida'),
          }),
        error: () =>
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.atencao'),
            detail: this.translate.instant('contador_operar.venda.nfe_erro'),
          }),
      });
  }

  private resetarFormulario(): void {
    this.comprador = undefined;
    // Sem isto a flag ficaria presa em `true` e o próximo comprador SINTÉTICO
    // seria considerado validável, acusando os três campos faltando — falso
    // positivo bloqueante. Ocorre no fluxo real: salvar() → resetarFormulario()
    // → carregarDados().
    this.compradorDetalhado = false;
    this.atualizarValidacaoComprador();
    // PONTO ÚNICO de reset do circuit breaker (junto com a troca de comprador em
    // `onCompradorSelecionado()`): o breaker é POR VENDA, e toda venda concluída
    // ou aba recarregada passa por aqui.
    // `clientesJaOferecidos` NÃO é limpo: ele é por SESSÃO da aba, e limpá-lo
    // faria o diálogo reabrir depois de cada venda para o mesmo cliente.
    this.retryAposCompletarUsado = false;
    this.mostrarCompletarCliente = false;
    this.origemCompletarCliente = null;
    this.vendedorSelecionadoId = null;
    this.timbradoSelecionadoId = null;
    this.mostrarSelecionarItem = false;
    this.carrinho = [];
    // `false` = NÃO restaurar a edição pendente. Um item carregado na faixa e
    // nunca confirmado não pode sobreviver à troca de cliente nem à venda
    // gravada (ele é do catálogo do cliente anterior), e restaurar aqui
    // devolveria uma linha a um carrinho que acabou de ser esvaziado — um item
    // fantasma.
    this.linhaLancamento?.limparComposicao(false);
    this.condicaoPagamento = 'CONTADO';
    this.numeroParcelas = 1;
    this.observacao = '';
  }
}
